import { useId, useState } from 'react'
import Modal from '../Modal'
import PromptMarkdownEditor from './PromptMarkdownEditor'

export interface PromptFields {
  title: string
  description: string | null
  body: string
}

interface PromptEditorDialogProps {
  title: string
  initial: PromptFields
  onSave: (fields: PromptFields) => Promise<void>
  onClose: () => void
}

/**
 * Shared metadata/content editor for independently owned prompts: Title,
 * optional Description, Markdown body. Used directly for create/edit, and
 * by PromptCatalogDialog (prefilled from the selected source) for import/copy.
 * A save error keeps the dialog open with everything the user typed intact.
 */
export default function PromptEditorDialog({
  title,
  initial,
  onSave,
  onClose,
}: PromptEditorDialogProps) {
  const [fields, setFields] = useState<PromptFields>(initial)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const titleErrorId = useId()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (saving) return
    if (!fields.title.trim()) {
      setError('Title is required.')
      return
    }
    if (!fields.body.trim()) {
      setError('Prompt content is required.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onSave({ ...fields, title: fields.title.trim() })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={title} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="prompt-editor-title" className="text-sm font-semibold text-harbor-text">
            Title
          </label>
          <input
            id="prompt-editor-title"
            type="text"
            value={fields.title}
            onChange={(e) => setFields((current) => ({ ...current, title: e.target.value }))}
            aria-describedby={error ? titleErrorId : undefined}
            aria-invalid={error !== null}
            className="mt-1 w-full rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-sm text-harbor-text"
          />
        </div>
        <div>
          <label
            htmlFor="prompt-editor-description"
            className="text-sm font-semibold text-harbor-text"
          >
            Description <span className="font-normal text-harbor-text-muted">(optional)</span>
          </label>
          <input
            id="prompt-editor-description"
            type="text"
            value={fields.description ?? ''}
            onChange={(e) =>
              setFields((current) => ({
                ...current,
                description: e.target.value.length > 0 ? e.target.value : null,
              }))
            }
            className="mt-1 w-full rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-sm text-harbor-text"
          />
        </div>
        <PromptMarkdownEditor
          id="prompt-editor-body"
          label="Prompt content (Markdown)"
          value={fields.body}
          onChange={(body) => setFields((current) => ({ ...current, body }))}
        />
        {error && (
          <p id={titleErrorId} role="alert" className="text-sm text-red-400">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-harbor-border px-4 py-2 text-sm font-medium text-harbor-text-secondary hover:bg-harbor-surface-raised disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
